import React, { useState } from 'react';
import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';

import type { CoverPictureId } from '../pageCoverPictureTypes.ts';
import {
	COVER_IMAGE_FIXED_HEIGHT,
	CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE,
} from '../pageCoverPictureConstants.ts';

import { ImageWrapper } from './ImageWrapper.tsx';

type UnsplashLoadingDivProps = {
	hasUnsplashImageLoaded: boolean;
	dynamicCoverImageHeight?: number;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashLoadingDiv = styled.div<UnsplashLoadingDivProps>({
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: (props) => `${props.dynamicCoverImageHeight}px`,
	width: '100%',
	background: token('color.background.accent.gray.subtlest'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	opacity: (props) => (props.hasUnsplashImageLoaded ? 0 : 1),
	borderRadius: 'unset',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnsplashWrapper = styled.div<{
	hasUnsplashImageLoaded: boolean;
	dynamicCoverImageHeight?: number;
	isFixedWidthImageOption?: boolean;
}>({
	display: 'flex',
	width: '100%',
	position: 'absolute',
	borderRadius: 'unset',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minHeight: (props) => {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		if (expValEquals('confluence_ttvc_fix_dynamic_cover_image_unsplash', 'isEnabled', true)) {
			if (props.isFixedWidthImageOption) {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				return `${COVER_IMAGE_FIXED_HEIGHT}px`;
			}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
			return `${CALCULATE_SCALED_COVER_IMAGE_HEIGHT_FOR_EDIT_PAGE}px`;
		}
		return `${props.dynamicCoverImageHeight}px`;
	},
});

export type UnsplashCoverPictureProps = {
	coverPicture: React.MutableRefObject<{ draft: CoverPictureId | null }>;
	dynamicCoverImageHeight?: number;
	isFixedWidthImageOption?: boolean;
	coverPictureDataPosition?: number;
	coverPictureDataId?: string;
};

export const UnsplashCoverPicture = ({
	coverPicture,
	dynamicCoverImageHeight,
	isFixedWidthImageOption,
	coverPictureDataPosition,
	coverPictureDataId,
}: UnsplashCoverPictureProps) => {
	const [hasUnsplashImageLoaded, setHasUnsplashImageLoaded] = useState<boolean>(false);

	return (
		<UnsplashWrapper
			hasUnsplashImageLoaded={hasUnsplashImageLoaded}
			dynamicCoverImageHeight={dynamicCoverImageHeight}
			isFixedWidthImageOption={isFixedWidthImageOption}
			data-testid="unsplash-wrapper"
		>
			{!expValEquals('confluence_ttvc_fix_dynamic_cover_image_unsplash', 'isEnabled', true) && (
				<UnsplashLoadingDiv
					hasUnsplashImageLoaded={hasUnsplashImageLoaded}
					dynamicCoverImageHeight={dynamicCoverImageHeight}
					data-testid="unsplash-loading-div"
				/>
			)}
			<ImageWrapper
				data-testid="unsplash-cover-picture"
				id="actual-cover-picture"
				data-vc="unsplash-cover-picture-editor"
				src={coverPicture.current.draft?.value.id ?? coverPictureDataId}
				coverPicturePosition={
					coverPicture.current.draft?.value.position ?? coverPictureDataPosition
				}
				onLoad={() => {
					setHasUnsplashImageLoaded(true);
				}}
				isFixedWidthImageOption={isFixedWidthImageOption}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
				className="cover-picture-wrapper-dangerously-set-inner-html"
			/>
		</UnsplashWrapper>
	);
};
